import { useRef } from 'react';
import { breakpoints, Button, Stack } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import type { TripType } from '@eti/schema-types';
import { css } from '@eti/styles';
import { Plus } from '@phosphor-icons/react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { DEPARTURE_DATE, DESTINATION, MULTI_BOUND, ORIGIN } from '../constants/formFields';
import { MAX_BOUNDS } from '../constants/multistop';
import type { DateFormat } from '../types/dateFormat';
import BoundHeader from './BoundHeader';
import BoundNew from './BoundNew';

const addBoundButtonStyles = css`
  width: 100%;

  @media (min-width: ${breakpoints._768}) {
    width: fit-content;
  }
`;

type BoundsNewProps = {
  dateFormat: DateFormat;
  selectedTripType: typeof TripType.MultiStop | typeof TripType.OneWay | typeof TripType.Return;
};

const getKey = (index: number) => `bound-${index}`;

const BoundsNew = ({ dateFormat, selectedTripType }: BoundsNewProps) => {
  const { t } = useTranslation();
  const addBoundButtonRef = useRef<HTMLButtonElement>(null);
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: MULTI_BOUND,
  });

  const fieldsWithIds = fields.map((field, index) => ({ field, id: index }));
  const hasRemoveButtons = fieldsWithIds.length > 2;

  const onAdd = () => {
    append({ [ORIGIN]: null, [DESTINATION]: null, [DEPARTURE_DATE]: null });
  };

  const handleOnRemove = (boundId?: number) => {
    remove(boundId);
    addBoundButtonRef.current?.focus();
  };

  return (
    <>
      {fieldsWithIds.map((_, index) => (
        <Stack key={getKey(index)}>
          <BoundHeader boundIndex={index} onRemove={handleOnRemove} />
          <BoundNew
            boundIndex={index}
            dateFormat={dateFormat}
            hasRemoveButtons={hasRemoveButtons}
            onRemove={handleOnRemove}
            selectedTripType={selectedTripType}
          />
        </Stack>
      ))}
      {fields.length < MAX_BOUNDS && (
        <Button
          className={addBoundButtonStyles}
          data-testid="searchForm-multiStop-addTrip-button"
          iconPrefix={<Plus />}
          onClick={onAdd}
          ref={addBoundButtonRef}
          size="small"
        >
          {t('Air.Search.MultiBound.AddBoundButton.Text')}
        </Button>
      )}
    </>
  );
};

export default BoundsNew;
